import { createContext, useContext, useId } from 'react';

const SectionBadgeContext = createContext(null);

const BRAND = '#004A53';
const PRIMARY = '#005654';
const INTERACTION = '#026F6D';
const ACCENT_LIGHT = '#f0f7f6';

export function SectionCard({ title, badge, children }) {
  return (
    <div style={{ marginBottom: '1.5rem', padding: '1.5rem', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12 }}>
      {(title || badge) && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: '1rem' }}>
          {title && <h3 className="h3" style={{ margin: 0, color: BRAND }}>{title}</h3>}
          {badge === 'Pflicht' && <Badge type="required" />}
          {badge === 'optional' && <Badge type="optional" />}
        </div>
      )}
      <SectionBadgeContext.Provider value={badge || null}>{children}</SectionBadgeContext.Provider>
    </div>
  );
}

export function Badge({ type }) {
  if (type === 'required') return <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 999, background: '#fef3c7', color: '#92400e', fontWeight: 600 }}>Pflicht</span>;
  return <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 999, background: '#f1f5f9', color: '#64748b', fontWeight: 600 }}>optional</span>;
}

export function Hint({ children, type = 'info' }) {
  const styles = {
    info: { bg: ACCENT_LIGHT, border: '#b7cfcc', text: BRAND, icon: 'ℹ' },
    warning: { bg: '#fffbeb', border: '#fde68a', text: '#92400e', icon: '⚠' },
    success: { bg: '#f0fdf4', border: '#bbf7d0', text: '#166534', icon: '✓' },
  };
  const c = styles[type] || styles.info;
  return (
    <div style={{ marginTop: 12, padding: '10px 14px', borderRadius: 8, background: c.bg, border: `1px solid ${c.border}`, display: 'flex', gap: 8, alignItems: 'flex-start' }}>
      <span style={{ color: c.text, fontWeight: 700, flexShrink: 0 }}>{c.icon}</span>
      <p style={{ margin: 0, fontSize: 13, color: c.text, lineHeight: 1.5 }}>{children}</p>
    </div>
  );
}

export function ChoiceCard({ label, description, selected, onClick, disabled, multiSelect }) {
  return (
    <button
      type="button"
      onClick={!disabled ? onClick : undefined}
      disabled={disabled}
      style={{
        background: selected ? ACCENT_LIGHT : '#f8fafc',
        border: selected ? `1.5px solid ${PRIMARY}` : '1px solid #e2e8f0',
        borderRadius: 10,
        padding: '0.875rem 1rem',
        cursor: disabled ? 'not-allowed' : 'pointer',
        display: 'flex', alignItems: 'center', gap: 10,
        textAlign: 'left', transition: 'all 0.15s',
        opacity: disabled ? 0.6 : 1,
        fontFamily: 'inherit',
      }}
    >
      <div style={{
        width: 18, height: 18, borderRadius: multiSelect ? 4 : '50%',
        border: `2px solid ${selected ? PRIMARY : '#d1d5db'}`,
        background: selected ? PRIMARY : 'white',
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      }}>
        {selected && <span style={{ color: 'white', fontSize: 10, fontWeight: 800 }}>✓</span>}
      </div>
      <div>
        <div style={{ fontSize: 14, fontWeight: 600, color: selected ? BRAND : '#1e293b' }}>{label}</div>
        {description && <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{description}</div>}
      </div>
    </button>
  );
}

export function UnitToggle({ value, onChange, options, disabled, disabledOptions = [] }) {
  return (
    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
      {options.map(opt => {
        const isOptionDisabled = disabledOptions.includes(opt.value) || disabled;
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => !isOptionDisabled && onChange(opt.value)}
            disabled={isOptionDisabled}
            style={{
              padding: '6px 16px', borderRadius: 6,
              border: `1.5px solid ${value === opt.value ? PRIMARY : '#e2e8f0'}`,
              background: value === opt.value ? PRIMARY : isOptionDisabled ? '#f1f5f9' : 'white',
              color: value === opt.value ? 'white' : isOptionDisabled ? '#cbd5e1' : '#374151',
              fontSize: 13, fontWeight: value === opt.value ? 700 : 400,
              cursor: isOptionDisabled ? 'not-allowed' : 'pointer', transition: 'all 0.15s',
              fontFamily: 'inherit',
              opacity: isOptionDisabled ? 0.6 : 1,
            }}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

export function SdxField({ as: Control = 'sdx-input', label, required = true, children, ...props }) {
  const id = useId();
  return (
    <div>
      <FieldLabel htmlFor={id} required={required} optional={!required}>{label}</FieldLabel>
      <Control {...props} id={id} aria-label={label} aria-required={required ? 'true' : undefined}>
        {children}
      </Control>
    </div>
  );
}

export function FieldLabel({ children, required, optional, htmlFor }) {
  const sectionBadge = useContext(SectionBadgeContext);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
      <label htmlFor={htmlFor} className="label" style={{ margin: 0, fontWeight: 500 }}>{children}</label>
      {required && sectionBadge !== 'Pflicht' && <Badge type="required" />}
      {optional && sectionBadge !== 'optional' && <Badge type="optional" />}
    </div>
  );
}

export { INTERACTION as ACCENT };
export { ACCENT_LIGHT };
export const ERROR_TEXT = { color: '#dc2626', fontSize: 13, marginTop: 4, display: 'block' };
export const WARN_TEXT = { color: '#d97706', fontSize: 13, marginTop: 4, display: 'block' };
